import React from 'react';
import { Stack, Divider } from '@mui/material';
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, TablePagination } from '@mui/material';
import { LedgerItemDetailDto, LedgerItemTypeEnum } from '@inosio/payments-sdk';
import NumberFormat from 'react-number-format';
import { convertCentsToDollars, convertSqlDateToUSCalendarDate } from '../../util/converters';
import SpinnerWithNoConfirmation from '../../Modal/spinnerWithNoConfirmation';
import LedgerErrorBody from './LedgerErrorBody';
import { ErrorType } from '../../../app/store';

export interface LedgerBodyProps {
  /**
   * List of ledger line items to be displayed
   */
  rows: LedgerItemDetailDto[];

  /**
   * Current page of the ledger item data book we are on
   */
  currentPage: number;

  /**
   * Callback fired when user changes the page
   */
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, pageNumber: number) => void;

  /**
   * The number of rows to be displayed on a single page
   */
  rowsPerPage: number;

  /**
   * Callback fired when the rows per page option is changed
   */
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

  /**
   * The total count of the line items in a single ledger
   */
  totalItems: number;

  /**
   * Boolean flag to show spinner
   */
  isFetchingLedgerBodyData: boolean;

  /**
   * Actual error object in case requests fails
   */
  ledgerBodyDataFetchError: ErrorType;

  /**
   * Boolean flag to show that the request failed
   */
  isFetchingLedgerBodyDataFailure: boolean;
}

/**
 * This goal of creating this component was to render a  list of ledger items to a user(resident, owner, pm etc.).
 * A ledger is a history of transactions between Property Managers and Owners/Residents.
 * This component's sole responsibility is just to display ledger line items, any state should be managed in the parent component.
 */
export default function LedgerBody(props: LedgerBodyProps) {
  return (
    <Stack overflow="hidden" height="100%" zIndex={0}>
      <Divider sx={{ borderColor: '#A8A8A8' }} />
      {props.isFetchingLedgerBodyDataFailure ? (
        <LedgerErrorBody {...props.ledgerBodyDataFetchError} />
      ) : props.isFetchingLedgerBodyData ? (
        <Stack id="ledger-spinner-container">
          <SpinnerWithNoConfirmation />
        </Stack>
      ) : (
        <Stack overflow="hidden" direction="column" height="100%" justifyContent="space-between">
          <TableContainer style={{ flex: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell id="subTitle">Date</TableCell>
                  <TableCell id="subTitle">Description</TableCell>
                  <TableCell id="subTitle">Debit</TableCell>
                  <TableCell id="subTitle">Credit</TableCell>
                  <TableCell id="subTitle">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody style={{ left: '28px' }}>
                {props.rows.map((row: LedgerItemDetailDto) => (
                  <TableRow key={row.Id}>
                    <TableCell id="content">{convertSqlDateToUSCalendarDate(row.ItemDate)}</TableCell>
                    <TableCell id="content">{row.Description}</TableCell>
                    <TableCell id="content">
                      {row.Type === LedgerItemTypeEnum.Debit ? (
                        <NumberFormat
                          thousandsGroupStyle="thousand"
                          value={convertCentsToDollars(row.Amount)}
                          prefix="$"
                          decimalSeparator="."
                          displayType="text"
                          type="text"
                          thousandSeparator={true}
                          allowNegative={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell id="content">
                      {row.Type === LedgerItemTypeEnum.Credit ? (
                        <NumberFormat
                          thousandsGroupStyle="thousand"
                          value={convertCentsToDollars(row.Amount)}
                          prefix="$"
                          decimalSeparator="."
                          displayType="text"
                          type="text"
                          thousandSeparator={true}
                          allowNegative={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell id="content">
                      {row.EndingBalance != null? (
                        <NumberFormat
                          thousandsGroupStyle="thousand"
                          value={convertCentsToDollars(row.EndingBalance)}
                          prefix="$"
                          decimalSeparator="."
                          displayType="text"
                          type="text"
                          thousandSeparator={true}
                          allowNegative={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      ) : (
                        ''
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[50, 100, 200]}
            count={props.totalItems || 0}
            page={props.currentPage}
            onPageChange={props.handleChangePage}
            rowsPerPage={props.rowsPerPage}
            onRowsPerPageChange={props.handleChangeRowsPerPage}
          />
        </Stack>
      )}
    </Stack>
  );
}
